import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export enum JoinFlowStatus {
  IDLE = 'IDLE',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  PARTIAL_SUCCESS = 'PARTIAL_SUCCESS',
  FAILED = 'FAILED',
}

export interface TournamentJoinResult {
  tournamentId: string;
  lucraId: string;
  name: string;
  success: boolean;
  error?: string;
}

export interface JoinFlowState {
  status: JoinFlowStatus;
  parentTournamentId: string | null;
  tournamentsToJoin: Array<{
    id: string;
    lucraId: string;
    name: string;
    buyIn: number;
  }>;
  currentIndex: number;
  results: TournamentJoinResult[];
  error: string | null;
  startedAt: number | null;
  selectedChallengeIds: string[];
}

const initialState: JoinFlowState = {
  status: JoinFlowStatus.IDLE,
  parentTournamentId: null,
  tournamentsToJoin: [],
  currentIndex: 0,
  results: [],
  error: null,
  startedAt: null,
  selectedChallengeIds: [],
};

const joinFlowSlice = createSlice({
  name: 'joinFlow',
  initialState,
  reducers: {
    startJoinFlow: (
      state,
      action: PayloadAction<{
        parentTournamentId: string;
        tournaments: Array<{
          id: string;
          lucraId: string;
          name: string;
          buyIn: number;
        }>;
      }>,
    ) => {
      state.status = JoinFlowStatus.IN_PROGRESS;
      state.parentTournamentId = action.payload.parentTournamentId;
      state.tournamentsToJoin = action.payload.tournaments;
      state.currentIndex = 0;
      state.results = [];
      state.error = null;
      state.startedAt = Date.now();
    },
    advanceToNext: (state) => {
      state.currentIndex = state.currentIndex + 1;
    },
    recordSuccess: (
      state,
      action: PayloadAction<{
        tournamentId: string;
        lucraId: string;
        name: string;
      }>,
    ) => {
      state.results.push({
        ...action.payload,
        success: true,
      });
    },
    recordFailure: (
      state,
      action: PayloadAction<{
        tournamentId: string;
        lucraId: string;
        name: string;
        error: string;
      }>,
    ) => {
      state.results.push({
        ...action.payload,
        success: false,
      });
    },
    completeJoinFlow: (state) => {
      const allSuccess = state.results.every((r) => r.success);
      const someSuccess = state.results.some((r) => r.success);

      if (allSuccess) {
        state.status = JoinFlowStatus.COMPLETED;
      } else if (someSuccess) {
        state.status = JoinFlowStatus.PARTIAL_SUCCESS;
      } else {
        state.status = JoinFlowStatus.FAILED;
      }
    },
    setError: (state, action: PayloadAction<string>) => {
      state.status = JoinFlowStatus.FAILED;
      state.error = action.payload;
    },
    resetJoinFlow: () => initialState,
    updateTournamentsToJoin: (
      state,
      action: PayloadAction<
        Array<{
          id: string;
          lucraId: string;
          name: string;
          buyIn: number;
        }>
      >,
    ) => {
      state.tournamentsToJoin = action.payload;
    },
    toggleChallengeSelection: (state, action: PayloadAction<string>) => {
      const challengeId = action.payload;
      const index = state.selectedChallengeIds.indexOf(challengeId);
      if (index === -1) {
        state.selectedChallengeIds.push(challengeId);
      } else {
        state.selectedChallengeIds.splice(index, 1);
      }
    },
    clearSelectedChallenges: (state) => {
      state.selectedChallengeIds = [];
    },
  },
});

export const {
  startJoinFlow,
  advanceToNext,
  recordSuccess,
  recordFailure,
  completeJoinFlow,
  setError,
  resetJoinFlow,
  updateTournamentsToJoin,
  toggleChallengeSelection,
  clearSelectedChallenges,
} = joinFlowSlice.actions;

export default joinFlowSlice.reducer;
